/**
 * Canvas に描くピンの見た目（サイズ・アンカー・描画処理）。
 *
 * ピン1本ごとにベクター描画すると件数に比例して重くなるため、
 * 解像度（devicePixelRatio × 印刷倍率）ごとに一度だけビットマップへ描いてキャッシュし、
 * 各ピンはそのビットマップを drawImage するだけにする。
 */
export interface CanvasPinIconOptions {
  /** ピン本体の画面上のサイズ（CSS px）。[幅, 高さ]。クリックの当たり判定にも使う */
  size: [number, number];
  /** ピン画像の左上を原点とした、地点に合わせる位置（CSS px）。L.Icon の iconAnchor と同じ */
  anchor: [number, number];
  /** 地点を原点としたポップアップの出る位置（CSS px）。L.Icon の popupAnchor と同じ */
  popupAnchor: [number, number];
  /**
   * 影や外縁が size の外にはみ出す幅（CSS px）。ビットマップはこの分だけ四方に広く描く。
   * 当たり判定には含めない
   */
  bleed?: number;
  /** size の座標系でピンを描く。呼び出し側で解像度に合わせて拡大し、bleed 分ずらし済み */
  draw: (ctx: CanvasRenderingContext2D) => void;
}

export class CanvasPinIcon {
  readonly options: CanvasPinIconOptions;

  private readonly bitmaps = new Map<number, HTMLCanvasElement | null>();

  constructor(options: CanvasPinIconOptions) {
    this.options = options;
  }

  /** 影や外縁のはみ出し幅（CSS px） */
  get bleed(): number {
    return this.options.bleed ?? 0;
  }

  /**
   * 指定の倍率で描いたビットマップを返す。大きさは size に bleed を四方に足したもの。
   * Canvas が使えない環境（jsdom など）では null。
   * @param pixelRatio 画面の devicePixelRatio に印刷倍率を掛けたもの
   */
  getBitmap(pixelRatio: number): HTMLCanvasElement | null {
    const ratio = Math.max(1, Math.ceil(pixelRatio));
    const cached = this.bitmaps.get(ratio);
    if (cached !== undefined) return cached;

    const [width, height] = this.options.size;
    const { bleed } = this;
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil((width + bleed * 2) * ratio);
    canvas.height = Math.ceil((height + bleed * 2) * ratio);
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      this.bitmaps.set(ratio, null);
      return null;
    }
    ctx.scale(ratio, ratio);
    ctx.translate(bleed, bleed);
    this.options.draw(ctx);
    this.bitmaps.set(ratio, canvas);
    return canvas;
  }
}
